/**
 * FROM INVENTORY (LONG SIDE) — right column.
 *
 * Mirror Access form `frmMatchingPSMatch_Stock_Sales` (cột phải L=9588/12012).
 * Order theo Top position trong Access:
 *   Section header → Stack ID combo → mini-header "Unmatched Inventory"
 *   → Instructed (stock_quantity) → Quality → Warehouse → Date In
 *   → Allocated (stock_matched) → Unallocated (stock_unmatched).
 */
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'

import type { StockSalesStackOption } from '@/store/api/matchingPsApi'

import { formatStandard } from './stockSalesUtils'

interface Props {
  stackOptions: StockSalesStackOption[]
  stackValue: StockSalesStackOption | null
  onStackChange: (opt: StockSalesStackOption | null) => void
  stockDateIn: string
  stockWarehouse: string
  stockQuality: string
  stockQuantity: number | null
  stockMatched: number | null
  stockUnmatched: number | null
}

// xs: thu label lại cho input đủ chỗ trên phone; sm+ giữ 110 (desktop 1:1 Access).
const labelSx = {
  width: { xs: 96, sm: 110 },
  flexShrink: 0,
  fontSize: { xs: '0.72rem', sm: '0.78rem' },
}
const compactInputSx = { '& .MuiInputBase-input': { py: 0.5 } }
const highlightInputSx = {
  '& .MuiInputBase-input': {
    py: 0.5,
    color: '#1F467D',
    fontWeight: 700,
  },
  '& .MuiOutlinedInput-root': { bgcolor: '#E9ECED' },
}
const compactAutocompleteSx = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: 0, pl: '6px' },
  '& .MuiAutocomplete-input': { py: '3px !important', fontSize: '0.8rem' },
}
const readonlyTextField = {
  size: 'small' as const,
  InputProps: { readOnly: true, sx: { fontSize: '0.8rem' } },
  fullWidth: true,
  sx: compactInputSx,
}
const highlightReadonlyTextField = {
  size: 'small' as const,
  InputProps: { readOnly: true, sx: { fontSize: '0.8rem' } },
  fullWidth: true,
  sx: highlightInputSx,
}

export default function StockSidePanel({
  stackOptions,
  stackValue,
  onStackChange,
  stockDateIn,
  stockWarehouse,
  stockQuality,
  stockQuantity,
  stockMatched,
  stockUnmatched,
}: Props) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          lineHeight: 1.3,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        From Inventory (Long Side)
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Stack ID
        </Typography>
        <Autocomplete
          options={stackOptions}
          value={stackValue}
          onChange={(_, v) => onStackChange(v)}
          getOptionLabel={(o) => o.stack_id ?? ''}
          isOptionEqualToValue={(a, b) => a.stack_id === b.stack_id}
          // Search theo cả warehouse/quality hiện ở các cột dropdown — getOptionLabel
          // chỉ trả stack_id nên default filter không match được chúng.
          filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase()
            if (!q) return opts
            return opts.filter((o) =>
              `${o.stack_id ?? ''} ${o.warehouse_short ?? ''} ${o.warehouse ?? ''} ${o.quality_code ?? ''}`
                .toLowerCase()
                .includes(q))
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.stack_id}>
              <Stack direction="row" spacing={1} sx={{ width: '100%', fontSize: '0.8rem' }}>
                {/* xs: popper hẹp theo anchor — cột chính co giãn + ellipsis,
                    cột quality ẩn để không tràn popper. */}
                <Box
                  component="span"
                  sx={{
                    flex: { xs: '1 1 auto', sm: '0 0 140px' },
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.stack_id}
                </Box>
                <Box
                  component="span"
                  sx={{ flex: { xs: '0 0 90px', sm: '0 0 120px' }, color: '#666' }}
                >
                  {option.warehouse_short ?? ''}
                </Box>
                <Box
                  component="span"
                  sx={{ flex: 1, color: '#666', display: { xs: 'none', sm: 'block' } }}
                >
                  {option.quality_code ?? ''}
                </Box>
              </Stack>
            </li>
          )}
          size="small"
          fullWidth
          sx={compactAutocompleteSx}
          renderInput={(params) => <TextField {...params} />}
        />
      </Stack>

      {/* Floating mini-header trên row Instructed (Access T=1140) */}
      <Typography
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          fontSize: '0.7rem',
          mt: 0,
        }}
      >
        Unmatched Inventory
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Instructed
        </Typography>
        <TextField
          {...highlightReadonlyTextField}
          value={stockQuantity == null ? '' : formatStandard(stockQuantity)}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Quality
        </Typography>
        <TextField {...readonlyTextField} value={stockQuality} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Warehouse
        </Typography>
        <TextField {...readonlyTextField} value={stockWarehouse} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Date In
        </Typography>
        <TextField {...readonlyTextField} value={stockDateIn} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Allocated
        </Typography>
        <TextField
          {...highlightReadonlyTextField}
          value={stockMatched == null ? '' : formatStandard(stockMatched)}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Unallocated
        </Typography>
        <TextField
          {...highlightReadonlyTextField}
          value={stockUnmatched == null ? '' : formatStandard(stockUnmatched)}
        />
      </Stack>
    </Stack>
  )
}
